import { z } from "zod/v4";
import { fetchApi } from "./internal";

/** Wire format for a version floor: "major.minor.patch". */
export const APP_VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export function isAppVersionString(value: string): boolean {
	return APP_VERSION_PATTERN.test(value);
}

/** Every slot the API always returns, regardless of whether it has a policy yet. */
export const APP_PLATFORMS = ["any", "ios", "android"] as const;
export type AppPlatform = (typeof APP_PLATFORMS)[number];

/** `null` is "no floor" / "nothing recommended" - explicitly open, not unset. */
const appVersionValueSchema = z
	.string()
	.refine(isAppVersionString, "must be major.minor.patch")
	.nullable();

export const appVersionPolicySchema = z.object({
	minVersion: appVersionValueSchema,
	platform: z.enum(APP_PLATFORMS),
	recommendedVersion: appVersionValueSchema,
	// `null` until the slot is ever saved - `updatedBy` is the presence guard.
	updatedAt: z.string().nullable(),
	updatedBy: z.string(),
});
export type AppVersionPolicy = z.infer<typeof appVersionPolicySchema>;

/**
 * `"environment"` means no row has ever been written and the legacy `.env` keys
 * are still deciding - the table is not yet in charge.
 */
export const appVersionPolicySourceSchema = z.enum(["database", "environment"]);
export type AppVersionPolicySource = z.infer<typeof appVersionPolicySourceSchema>;

export const appVersionPoliciesSchema = z.object({
	policies: z.array(appVersionPolicySchema),
	source: appVersionPolicySourceSchema,
});
export type AppVersionPolicies = z.infer<typeof appVersionPoliciesSchema>;

export const updateAppVersionPolicySchema = z.object({
	minVersion: appVersionValueSchema,
	recommendedVersion: appVersionValueSchema,
});
export type UpdateAppVersionPolicy = z.infer<typeof updateAppVersionPolicySchema>;

export const apps = {
	list: () => fetchApi("/admin/apps", appVersionPoliciesSchema),
	// async so an invalid version rejects the returned promise instead of
	// throwing synchronously out of `updateAppVersionPolicySchema.parse`.
	update: async (platform: AppPlatform, body: UpdateAppVersionPolicy) =>
		fetchApi(`/admin/apps/${platform}`, appVersionPolicySchema, {
			body: JSON.stringify(updateAppVersionPolicySchema.parse(body)),
			method: "PUT",
		}),
};
